export const ACQUISITION_DEMO_FRAME_MESSAGE_TYPE = "chickytutor.acquisition-demo.frame-resize.v1";
export const ACQUISITION_DEMO_ID = "acquisition-speaking-demo";

export type AcquisitionDemoFramePhase = "pre_session" | "active";

export type AcquisitionDemoFrameResizeMessage = {
  type: typeof ACQUISITION_DEMO_FRAME_MESSAGE_TYPE;
  demoId: typeof ACQUISITION_DEMO_ID;
  phase: AcquisitionDemoFramePhase;
  height: number;
};

const MIN_FRAME_HEIGHT = 768;
const MAX_FRAME_HEIGHT = 1280;
const ACTIVE_FRAME_HEIGHT = 840;
const PRE_SESSION_BOTTOM_PADDING = 32;

type FrameHeightInput = {
  phase: AcquisitionDemoFramePhase;
  viewportHeight: number;
  contentBottom?: number | null;
  scrollOffset?: number;
};

type MessageEventLike = {
  data: unknown;
  origin: string;
  source: unknown;
};

export function clampAcquisitionDemoFrameHeight(height: number): number {
  if (!Number.isFinite(height)) return MIN_FRAME_HEIGHT;
  return Math.min(MAX_FRAME_HEIGHT, Math.max(MIN_FRAME_HEIGHT, Math.ceil(height)));
}

export function getAcquisitionDemoFrameHeight({
  contentBottom,
  phase,
  scrollOffset = 0,
  viewportHeight,
}: FrameHeightInput): number {
  if (phase === "pre_session" && Number.isFinite(contentBottom)) {
    return clampAcquisitionDemoFrameHeight(
      Number(contentBottom) + scrollOffset + PRE_SESSION_BOTTOM_PADDING
    );
  }

  return clampAcquisitionDemoFrameHeight(Math.max(viewportHeight, ACTIVE_FRAME_HEIGHT));
}

export function createAcquisitionDemoFrameResizeMessage(
  phase: AcquisitionDemoFramePhase,
  height: number
): AcquisitionDemoFrameResizeMessage {
  return {
    type: ACQUISITION_DEMO_FRAME_MESSAGE_TYPE,
    demoId: ACQUISITION_DEMO_ID,
    phase,
    height: clampAcquisitionDemoFrameHeight(height),
  };
}

export function isAcquisitionDemoFrameResizeMessage(
  value: unknown
): value is AcquisitionDemoFrameResizeMessage {
  if (!value || typeof value !== "object") return false;

  const candidate = value as Partial<AcquisitionDemoFrameResizeMessage>;
  return (
    candidate.type === ACQUISITION_DEMO_FRAME_MESSAGE_TYPE &&
    candidate.demoId === ACQUISITION_DEMO_ID &&
    (candidate.phase === "pre_session" || candidate.phase === "active") &&
    typeof candidate.height === "number" &&
    Number.isFinite(candidate.height) &&
    candidate.height >= MIN_FRAME_HEIGHT &&
    candidate.height <= MAX_FRAME_HEIGHT
  );
}

export function getTrustedAcquisitionDemoFrameResizeMessage(
  event: MessageEventLike,
  expectedOrigin: string,
  expectedSource: unknown
): AcquisitionDemoFrameResizeMessage | null {
  if (event.origin !== expectedOrigin || event.source !== expectedSource) return null;
  return isAcquisitionDemoFrameResizeMessage(event.data) ? event.data : null;
}
